import { UseFormRegister, UseFormWatch } from "react-hook-form";

import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import { Blueprint, BlueprintInput } from "types/generated";
import FormFieldViewText from "components/FormFields/ViewText";

import DeploymentFormField from "./FormField";
import TemplatePreviewSkeleton from "./Skeleton";
import styles from "./styles.module.css";
import { VersionTemplateFormFields } from "./types";

type DeploymentFormFormProps = {
  item: Blueprint;
  isPublished: boolean;
  hasErrors: boolean;
  hasPreview: boolean;
  loading?: boolean;
  errors: string[];
  inputs: BlueprintInput[];
  /*
   * This is a callback to edit the description field, when not passed - the description will be not be visible if unavailable
   */
  handleEditDescription?: () => void;
  onOpenFullDescription: () => void;
  register?: UseFormRegister<VersionTemplateFormFields>;
  watch?: UseFormWatch<VersionTemplateFormFields>;
  isUpdateMode?: boolean;
};

const DeploymentForm = ({
  isPublished,
  hasPreview,
  hasErrors,
  errors,
  inputs,
  loading,
  register,
  watch,
  isUpdateMode,
}: DeploymentFormFormProps) => {
  const deploymentName = watch?.("name");

  return (
    <Box direction="column" fullWidth gap="large">
      {isPublished && register && (
        <>
          {isUpdateMode ? (
            <FormFieldViewText label="Name" value={deploymentName || ""} />
          ) : (
            <FormField label="Name">
              {({ ariaInputProps }) => (
                <Input
                  placeholder="Enter deployment name here..."
                  {...register("name", { required: "Name field is required." })}
                  {...ariaInputProps}
                />
              )}
            </FormField>
          )}
        </>
      )}

      {!isPublished && !hasPreview && !hasErrors && (
        <Banner variant="info">
          No preview available. Please add some inputs to the template.
        </Banner>
      )}

      {!isPublished && hasPreview && !hasErrors && (
        <Banner variant="info">
          This is just a template preview, you will not be able to create a stack from it.
        </Banner>
      )}

      {!isPublished &&
        hasErrors &&
        errors.map((error, index) => (
          <Banner aria-live="polite" className={styles.errorBanner} variant="danger" key={index}>
            {error}
          </Banner>
        ))}

      {loading && <TemplatePreviewSkeleton />}
      {!loading && !hasErrors && (
        <Box direction="column" grow="1" fullWidth gap="large">
          {inputs.map((input) => (
            <DeploymentFormField key={input.id} input={input} editMode={isUpdateMode} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DeploymentForm;
